<template>
  <div class="viewfinder">
    <div v-if="cameraErrorTranslationKey">
      <h1>{{ $t('error') }}</h1>
      <h2>{{ $t(`errors.${this.cameraErrorTranslationKey}`) }}</h2>
    </div>
    <QrcodeStream track :camera="camera" @decode="onDecode" @onInit="onInit">
      <div v-if="validationSuccess" class="material-input">
        <MaterialInputCard class="material-input-card" :user-id="userId" @close="resetCameraState" />
      </div>
      <div v-else class="overlay">
        <IosQrScanner w="100%" h="100%" />
        <div class="message" :class="{ hidden: !validationPending && !validationError && !userDoesNotExists }">
          <div v-if="validationPending">{{ $t('validating') }}</div>
          <div v-else-if="validationError" class="error">{{ $t('errors.validation-error') }}</div>
          <div v-else-if="userDoesNotExists" class="error">{{ $t('errors.invalid-user') }}</div>
        </div>
      </div>
    </QrcodeStream>
  </div>
</template>

<i18n>
  {
    "es": {
      "error": "Error",
      "validating": "Validando...",
      "errors": {
        "not-allowed-error": "Necesita permitirle acceso a la camara a la applicación.",
        "not-found-error": "No hay cámara en este dispositivo.",
        "not-supported-error": "Una conexión segura con los servidores es requerida para usar la cámara (HTTPS o localhost). Si está detrás de un proxy, intente desactivarlo.",
        "not-readable-error": "No se puede obtener información de la cámara. Puede ser que ya esté en uso por otra aplicación?",
        "overconstrained-error": "Las cámaras instaladas en el dispositivo no son adecuadas para el uso en la aplicación.",
        "stream-api-not-supported-error": "Ese navegador no soporta el uso de la cámara (Stream API no soportada)",
        "unknown-error": "Se produjo un error desconocido al intentar acceder a la cámara.",
        "validation-error": "Se produjo un error durante la validación. Por favor intente nuevamente.",
        "invalid-user": "El código de usuario escaneado es inválido o el usuario no existe."
      }
    },
    "en": {
      "error": "Error",
      "validating": "Validating...",
      "errors": {
        "not-allowed-error": "You need to grant camera access permisson.",
        "not-found-error": "No camera on this device.",
        "not-supported-error": "A secure connection to the servers is required to use the camera (HTTPS or localhost). If you are behind a proxy, try disabling it.",
        "not-readable-error": "Cannot read information from the camera. Is it already in use?",
        "overconstrained-error": "Installed cameras are not suitable.",
        "stream-api-not-supported-error": "This browser does not support camera use (Stream API is not supported).",
        "unknown-error": "An unknown error ocurred while trying to access the camera.",
        "validation-error": "An error ocurred during validation. Please try again again.",
        "invalid-user": "The scanned user code is invalid or the user is nonexistent."
      }
    }
  }
</i18n>

<script>
  import { QrcodeStream } from 'vue-qrcode-reader';
  import IosQrScanner from 'vue-ionicons/dist/ios-qr-scanner.vue';
  import gql from 'graphql-tag';

  import MaterialInputCard from '@/components/MaterialInputCard.vue';

  const ERROR_TRANSLATION_KEYS = {
    NotAllowedError: 'not-allowed-error',
    NotFoundError: 'not-found-error',
    NotSupportedError: 'not-supported-error',
    NotReadableError: 'not-readable-error',
    OverconstrainedError: 'overconstrained-error',
    StreamApiNotSupportedError: 'stream-api-not-supported-error',
  };

  export default {
    name: 'qr-code-scanner',
    components: {
      QrcodeStream,
      IosQrScanner,
      MaterialInputCard,
    },
    data() {
      return {
        cameraErrorTranslationKey: '',
        cameraActive: true,
        isValid: undefined,
        userId: undefined,
      };
    },
    computed: {
      camera() {
        return this.cameraActive ? 'auto' : 'off';
      },
      validationPending() {
        return this.isValid === undefined
          && this.cameraActive === false;
      },
      validationSuccess() {
        return this.isValid === true;
      },
      validationError() {
        return this.isValid === null;
      },
      userDoesNotExists() {
        return this.isValid === false;
      },
    },
    methods: {
      turnCamera(on) {
        this.cameraActive = on;
      },
      resetCameraState() {
        this.isValid = undefined;
        this.turnCamera(true);
      },
      validationStarted() {
        this.turnCamera(false);
      },
      validationFailed(withError = false) {
        this.isValid = withError ? null : false;
        setTimeout(() => this.resetCameraState(), 2000);
      },
      validationSuccessful(userId) {
        this.isValid = true;
        this.userId = userId;
      },
      async validateThatUserExists(userId) {
        const { data } = await this.$apollo.query({
          query: gql`query validateUserExistence($input: ValidateUserExistenceInput!) {
            validateUserExistence(input: $input)
          }`,
          variables: {
            input: {
              userId,
            },
          },
          fetchPolicy: 'network-only',
        });
        return data.validateUserExistence;
      },
      async onInit(promise) {
        try {
          await promise;
          this.resetCameraState();
        } catch (error) {
          const translationKey = ERROR_TRANSLATION_KEYS[error.name];
          this.cameraErrorTranslationKey = translationKey || 'unknown-error';
        }
      },
      async onDecode(result) {
        if (!result) return;
        this.validationStarted();

        try {
          const isValid = await this.validateThatUserExists(result);
          if (!isValid) {
            this.validationFailed();
          } else {
            this.validationSuccessful(result);
          }
        } catch (err) {
          console.error(err);
          this.validationFailed(true);
        }
      },
    },
  };
</script>

<style lang="scss">
  @import '~vue-ionicons/ionicons.scss';
</style>

<style lang="scss" scoped>
  .viewfinder {
    width: 100%;
    height: 100%;
  }

  .overlay {
    width: 100%;
    height: 100%;
    color: white;

    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    box-sizing: border-box;
    padding: 1em;
  }

  .message {
    height: 3em;
    background-color: rgba(0, 0, 0, 0.65);
    border-radius: 1em;
    text-align: center;

    display: flex;
    justify-content: center;
    align-items: center;
    padding: 1em;
  }

  .hidden {
    visibility: hidden;
  }

  .error {
    color: red;
  }

  .material-input {
    width: 100%;
    height: 100%;

    display: flex;
    flex-direction: column;
    justify-content: stretch;
    align-items: stretch;
    box-sizing: border-box;
    padding: 0.75em;
  }

  .material-input-card {
    flex-grow: 1;
  }
</style>
